"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { authService } from "@/services/api"
import Navbar from "@/components/Navbar"
import Footer from "@/components/Footer"
import { User, Calendar, Mail, Phone, MapPin, Edit } from "lucide-react"
import { useLanguage } from "@/contexts/LanguageContext"

// Profile API response interface based on the provided structure
interface ProfileResponse {
  success: boolean
  errorLevel: string
  message: string
  code: number
  data: {
    success: boolean
    code: number | null
    message: string | null
    result: {
      gmtCreated: number
      gmtModified: number
      status: string
      id: number
      account: string
      nickname: string | null
      gender: string
      avatar: string | null
      email: string | null
      phoneNumber: string | null
      phoneAreaCode: string | null
      gmtBirthday: number | null
      isBlocked: boolean
      isLocked: boolean
      realName: string | null
      language: string | null
      isUsernameFroze: boolean
      isNeedDestroy: boolean
      gmtDestroyTime: number | null
      gmtDestroyRequest: number | null
      isUnderDestroy: boolean | null
      gmtDestroy: number | null
      userMetaModel: any | null
      departmentModelList: any[] | null
      roleModelList: any[] | null
    }
    serverTime: number
  }
}

export default function ProfilePage() {
  const [profile, setProfile] = useState<ProfileResponse['data']['result'] | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { t } = useLanguage()

  useEffect(() => {
    const token = localStorage.getItem("token")
    if (!token) {
      navigate("/login")
      return
    }

      const fetchProfile = async () => {
    try {
      // Use HttpService directly to get the raw response structure
      const response = await import("@/services/http").then(({ HttpService }) => 
        HttpService.get("/uc/profile")
      )
      
      // Handle the actual API response structure
      if (response && typeof response === 'object' && 'result' in response) {
        setProfile(response.result)
      } else {
        toast.error("Failed to load profile data")
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        localStorage.removeItem("token")
        navigate("/login")
      } else {
        toast.error("Failed to load profile data")
      }
    } finally {
      setLoading(false)
    }
  }

    fetchProfile()
  }, [navigate])

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return "N/A"
    return new Date(timestamp).toLocaleDateString()
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "NORMAL":
        return <Badge variant="default" className="bg-green-100 text-green-800">{t('profile.status.normal')}</Badge>
      case "BLOCKED":
        return <Badge variant="destructive">{t('profile.status.blocked')}</Badge>
      case "LOCKED":
        return <Badge variant="secondary">{t('profile.status.locked')}</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const getGenderText = (gender: string) => {
    switch (gender) {
      case "MALE":
        return t('profile.gender.male')
      case "FEMALE":
        return t('profile.gender.female')
      case "UNKNOWN":
        return t('profile.gender.unknown')
      default:
        return gender
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-20 pb-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="h-64 bg-gray-200 rounded"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">{t('profile.title')}</h1>
            <p className="text-gray-600 mt-2">{t('profile.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 基本信息卡片 */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <User className="mr-2 h-5 w-5" />
                    {t('profile.basicInfo')}
                  </CardTitle>
                  <CardDescription>{t('profile.basicInfoDesc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">{t('profile.username')}</label>
                      <p className="text-sm text-gray-900 mt-1">{profile?.account || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">{t('profile.nickname')}</label>
                      <p className="text-sm text-gray-900 mt-1">{profile?.nickname || t('profile.notSet')}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">{t('profile.realName')}</label>
                      <p className="text-sm text-gray-900 mt-1">{profile?.realName || t('profile.notSet')}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">{t('profile.gender')}</label>
                      <p className="text-sm text-gray-900 mt-1">{getGenderText(profile?.gender || "UNKNOWN")}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">{t('profile.language')}</label>
                      <p className="text-sm text-gray-900 mt-1">{profile?.language || t('profile.notSet')}</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">{t('profile.accountStatus')}</label>
                      <div className="mt-1">{getStatusBadge(profile?.status || "UNKNOWN")}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* 联系信息卡片 */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Mail className="mr-2 h-5 w-5" />
                    {t('profile.contactInfo')}
                  </CardTitle>
                  <CardDescription>{t('profile.contactInfoDesc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 flex items-center">
                      <Mail className="mr-1 h-4 w-4" />
                      {t('profile.email')}
                    </label>
                    <p className="text-sm text-gray-900 mt-1">{profile?.email || t('profile.notSet')}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 flex items-center">
                      <Phone className="mr-1 h-4 w-4" />
                      {t('profile.phone')}
                    </label>
                    <p className="text-sm text-gray-900 mt-1">
                      {profile?.phoneAreaCode && profile?.phoneNumber 
                        ? `+${profile.phoneAreaCode} ${profile.phoneNumber}`
                        : t('profile.notSet')
                      }
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* 时间信息卡片 */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="mr-2 h-5 w-5" />
                    {t('profile.timeInfo')}
                  </CardTitle>
                  <CardDescription>{t('profile.timeInfoDesc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">{t('profile.registrationTime')}</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {formatDate(profile?.gmtCreated || null)}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">{t('profile.lastModified')}</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {formatDate(profile?.gmtModified || null)}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">{t('profile.birthday')}</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {formatDate(profile?.gmtBirthday || null)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="mt-8 flex justify-center">
            <Button onClick={() => navigate("/dashboard")} variant="outline" className="mr-4">
              {t('profile.backToDashboard')}
            </Button>
            <Button variant="outline">
              <Edit className="mr-2 h-4 w-4" />
              {t('profile.editProfile')}
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
